//checkpoint
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { User, Issue, ViewerRelation } from 'util/constants';
import { useUser } from 'util/UserContext';
import { Avatar, AvatarImage, AvatarFallback } from 'ui/Avatar';
import DetailPanel from 'ui/DetailPanel';
import api from 'config/axiosConfig';
import EntityAvatar from '../Entities/EntityAvatar';
import FollowButton from 'components/Common/FollowButton';
import FollowersModal from 'components/Common/FollowersModal';
import AlignmentTile from 'components/Common/AlignmentTile';
import { 
    Carousel, 
    CarouselContent, 
    CarouselItem, 
    CarouselNext, 
    CarouselPrevious 
} from 'ui/Carousel';
import { PrivacyLevel } from 'util/userUtils';
import { canViewUserFollow } from 'util/getObjectDetails';
import UserAlignmentModal from 'components/User/UserAlignmentModal';
import ScrollableTabsList from 'ui/ScrollableTabsList';
import UserBackgroundTile from './UserBackgroundTile';
import { Loader2 } from 'lucide-react';
import AlignmentSummaryTile from './AlignmentSummaryTile';

// Define statement interface
interface AlignmentStatement {
    id: number;
    statement: string;
}

interface SocialLink {
    type: 'custom' | 'instagram' | 'x' | 'facebook' | 'youtube' | 'tiktok' | 'reddit';
    label: string;
    url: string;
}

interface PropertiesPublic {
    avatar_url: string;
    avatar_key: string;
    about_bio_links?: SocialLink[];
    about_bio_brief?: string;
    about_alignment?: Record<string, string>;
    is_private?: boolean;
}

// Omit the original properties_public and extend with our new one
interface UserWithPublicProps extends Omit<User, 'properties_public'> {
    properties_public: PropertiesPublic;
    about: any;
    viewer_relation?: string;
    post_count?: number;
    follower_count?: number;
    following_count?: number;
}

interface UserProfilePlateProps {
    user: UserWithPublicProps;
    isDetailPanelOpen?: boolean;
    onOpenPanelChange?: (open: boolean) => void;
}

// Add interface for API alignment statement
interface ApiAlignmentStatement {
  id: number;
  statement_id: number;
  statement_text: string;
  is_active: boolean;
  properties: Record<string, any>;
  created_at: string;
  updated_at: string;
}

const UserProfilePlate: React.FC<UserProfilePlateProps> = ({ user, isDetailPanelOpen, onOpenPanelChange = () => {} }) => {
    const { currentUser, setShowAuthModal, awaitingUser } = useUser();
    const isOwnProfile = currentUser?.username === user.username;
    const [profileUser, setProfileUser] = useState<UserWithPublicProps | null>(user);
    const [primaryIssue, setPrimaryIssue] = useState<Issue | null>(null);
    const [followerCount, setFollowerCount] = useState<number>(0);
    const [followingCount, setFollowingCount] = useState<number>(0);
    const [postCount, setPostCount] = useState<number>(0);
    const [canViewFollow, setCanViewFollow] = useState<boolean>(false);
    const IMG_CDN = process.env.REACT_APP_IMAGE_CDN
    const [showFollowersModal, setShowFollowersModal] = useState(false);
    const [showFollowingModal, setShowFollowingModal] = useState(false);
    
    // Add state for API alignment statements
    const [apiAlignmentStatements, setApiAlignmentStatements] = useState<ApiAlignmentStatement[]>([]);
    const [isLoadingStatements, setIsLoadingStatements] = useState<boolean>(true);
    const [statementsError, setStatementsError] = useState<string | null>(null);
    const [showAlignmentModal, setShowAlignmentModal] = useState(false);
    const [statementNumber, setStatementNumber] = useState<number | null>(null);
    const [selectedProfileTab, setSelectedProfileTab] = useState<string>(
        user.about && typeof user.about === 'object' 
        && Object.keys(user.about).some(key => key.startsWith('about_alignment_answer')) ? 'perspective' : 'background'
    );
    const [carouselApi, setCarouselApi] = useState<any>(null);
    const [isPanelOpen, setIsPanelOpen] = useState(
        user.about && typeof user.about === 'object' 
        && (Object.keys(user.about).some(key => key.startsWith('about_alignment_answer')) || 
        Object.keys(user.about).some(key => key.startsWith('about_bio_gender')) || 
        Object.keys(user.about).some(key => key.startsWith('about_bio_age')) || 
        Object.keys(user.about).some(key => key.startsWith('about_bio_location')) || 
        Object.keys(user.about).some(key => key.startsWith('about_bio_religion')) || 
        Object.keys(user.about).some(key => key.startsWith('about_bio_education')) || 
        Object.keys(user.about).some(key => key.startsWith('about_bio_political_identities'))) ? true : false);

    // Derived alignment statements from API data
    const alignmentStatements: AlignmentStatement[] = React.useMemo(() => {
        if (apiAlignmentStatements.length === 0) {
            return [];
        }
        
        return apiAlignmentStatements
            .sort((a, b) => a.statement_id - b.statement_id)
            .map(statement => ({
                id: statement.statement_id,
                statement: statement.statement_text
            }));
    }, [apiAlignmentStatements]);
    
    // Fetch alignment statements from API
    useEffect(() => {
        const fetchAlignmentStatements = async () => {
            try {
                setIsLoadingStatements(true);
                const response = await api.get('/api/alignment/statements');
                if (response.data && response.data.status === 'success') {
                    setApiAlignmentStatements(response.data.data);
                } else {
                    setStatementsError('Failed to load alignment statements');
                    console.error('Error in alignment response:', response.data);
                }
            } catch (error) {
                console.error('Error fetching alignment statements:', error);
                setStatementsError('Failed to load alignment statements');
            } finally {
                setIsLoadingStatements(false);
            }
        };

        fetchAlignmentStatements();
    }, []);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const response = await api.get<UserWithPublicProps>(`/api/user/${user.username}`);
                setProfileUser(response.data);
                if (response.data.about?.about_bio_issue?.value) {
                    const issue = await api.get<Issue>(`/api/issues/id/${parseInt(response.data.about.about_bio_issue.value, 10)}`);
                    setPrimaryIssue(issue.data);
                }
                if (response.data.about && typeof response.data.about === 'object' 
                    && Object.keys(response.data.about).some(key => key.startsWith('about_alignment_answer'))) {
                    console.log("Setting perspective tab");
                    setSelectedProfileTab('perspective');
                    setIsPanelOpen(true);
                }
                const followerCount = await api.get<{ count: number }>(`/api/follow/user/${user.id}/followers/count`);
                setFollowerCount(followerCount.data.count);
                const followingCount = await api.get<{ count: number }>(`/api/follow/user/${user.id}/following/count`);
                setFollowingCount(followingCount.data.count);
                setCanViewFollow(await canViewUserFollow(response.data));
                const spillCount = await api.get<{ total: number }>(`/api/entities`, {
                        params: {
                            entityType: 'comment,impact,solution',
                            count: true,
                            coauthorId: user.id
                    }
                });
                setPostCount(spillCount.data.total);
            } catch (error) {
                console.error('Error fetching user:', error);
            }
        };
        fetchUser();
        //console.log("User profile plate rendered");
    }, [user]);

    const getLinkIcon = (type: string, url: string) => {
        if (!type) return null;
    
        switch (true) {
          case type === 'youtube':
            return (
              <span className={`size-4`}>
                <img className={`size-4`} src={`${IMG_CDN}/icons/youtube_icon.svg`} alt="YouTube" />
              </span>
            );
          case type === 'tiktok':
            return (
                <span className={`size-4`}>
                  <img className={`size-4 rounded-full bg-white`} src={`${IMG_CDN}/icons/tiktok_icon.svg`} alt="TikTok" />
                </span>
            );
          case type === 'instagram':
            return (
                <span className={`size-4`}>
                  <img className={`size-4`} src={`${IMG_CDN}/icons/instagram_icon.png`} alt="Instagram" />
                </span>
            );
          case type === 'x':
            return  (
                <span className={`size-4`}>
                  <img className={`size-4 rounded-full bg-white`} src={`${IMG_CDN}/icons/x_icon.png`} alt="X" />
                </span>
            );
          case type === 'facebook':
            return (
                <span className={`size-4`}>
                  <img className={`size-4`} src={`${IMG_CDN}/icons/facebook_icon.svg`} alt="Facebook" />
                </span>
            );
          case type === 'reddit':
            return (
                <span className={`size-4`}>
                  <img className={`size-4`} src={`${IMG_CDN}/icons/reddit_icon.svg`} alt="Reddit" />
                </span>
            );
          case type === 'custom':
            try {
                const linkHostname = new URL(url).hostname;
                if (linkHostname) {
                    return (
                        <span className={`size-4`}>
                            <img 
                                className={`size-4 rounded-sm`} 
                                src={`https://${linkHostname}/favicon.ico`} 
                                alt={`${linkHostname} icon`} 
                                onError={(e) => {
                                    // When primary favicon fails, try faviconkit as fallback
                                    e.currentTarget.onerror = null; // Prevent infinite fallback loop
                                    e.currentTarget.src = `https://api.faviconkit.com/${linkHostname}`;
                                }}
                            />
                        </span>
                    );
                }
            } catch (error) {
                console.error("Error parsing URL:", error);
            }
            // Default icon if URL parsing fails
            return (
                <span className={`size-4`}>
                    <img className={`size-4 rounded-sm`} src={`${IMG_CDN}/icons/link_icon.svg`} alt="Link" />
                </span>
            );
          default:    
            return null;
        }
      };

    // Function to get alignment data for a specific statement number
    const getAlignmentData = (statementNumber: number) => {
        if (!profileUser?.about) return null;
        
        const prefix = `about_alignment_answer_${statementNumber}`;
        
        // Make sure profileUser.about[prefix] exists
        if (!profileUser.about[prefix]) {
            //console.log(`No alignment data found for prefix: ${prefix}`);
            return null;
        }
        
        // Parse the data based on the structure
        try {
            // Safely access the value, checking if it exists first
            if (!profileUser.about[prefix].value) {
                //console.log(`No value found for prefix: ${prefix}`);
                return null;
            }
            
            const parsedValue = JSON.parse(profileUser.about[prefix].value);
            //console.log("Parsed value for statement", statementNumber, parsedValue);
            
            return {
                response: parseInt(parsedValue.response || "0", 10),
                importance: parseInt(parsedValue.importance || "0", 10),
                note: parsedValue.note || "",
                privacy: (profileUser.about[prefix].privacy || parsedValue.privacy || "public") as PrivacyLevel
            };
        } catch (error) {
            console.error(`Error processing alignment data for statement ${statementNumber}:`, error);
            return null;
        }
    };

    // Function to get the current user's alignment data
    const getCurrentUserAlignmentData = (statementNumber: number) => {
        if (!currentUser?.properties) return null;

        //console.log("Getting current user alignment data for statement", statementNumber);
        const prefix = `about_alignment_answer_${statementNumber}`;
        
        // Make sure the data path exists
        if (!currentUser.properties[prefix]) {
            return null;
        }
        
        // Make sure the value exists
        if (!currentUser.properties[prefix].value) {
            return null;
        }
        
        // Parse the data based on the structure
        try {
            const parsedValue = JSON.parse(currentUser.properties[prefix].value);
            
            return {
                response: parseInt(parsedValue.response || "0", 10),
                importance: parseInt(parsedValue.importance || "0", 10)
            };
        } catch (error) {
            console.error(`Error processing current user alignment data for statement ${statementNumber}:`, error);
            return null;
        }
    };

    const handleRespond = (statementId: number) => {
        if (!awaitingUser && !currentUser) {
            setShowAuthModal(true);
        } else {
            setStatementNumber(statementId);
            setShowAlignmentModal(true);
        }
    };

    // Function to handle alignment statement selection
    const handleAlignmentStatementChange = (statementId: number | string) => {
        if (statementId === "summary") {
            // Navigate to summary
            if (carouselApi) {
                carouselApi.scrollTo(0);
            }
            return;
        }
        
        // Convert to number if it's a string
        const numericId = typeof statementId === 'string' ? parseInt(statementId, 10) : statementId;
        
        // Find the index of the statement in the array
        const visibleStatements = alignmentStatements.filter(s => {
            const alignmentData = getAlignmentData(s.id);
            return alignmentData !== null;
        });
        
        const index = visibleStatements.findIndex(s => s.id === numericId);
        if (index !== -1 && carouselApi) {
            // Add 1 to account for the summary tile at index 0
            carouselApi.scrollTo(index + 1);
        }
    };

    // Function to render alignment tiles for statements with responses
    const renderAlignmentTiles = () => {
        if (isLoadingStatements) {
            return (
                <div className="flex justify-center items-center p-4">
                    <Loader2 className="size-4 animate-spin" />
                </div>
            );
        }

        if (statementsError || alignmentStatements.length === 0) {
            return (
                <div className="text-sm text-center py-2">
                    Unable to load alignment statements
                </div>
            );
        }

        // Create arrays to hold answered and unanswered items separately
        const answeredItems: React.ReactNode[] = [];
        const unansweredItems: React.ReactNode[] = [];
        
        // Create arrays to store visible statements for the dropdown, also separated
        const answeredStatements: Array<{id: number, statement: string, hasResponse: boolean}> = [];
        const unansweredStatements: Array<{id: number, statement: string, hasResponse: boolean}> = [];
        
        // Get full statements data with category info
        const statementsWithCategory = alignmentStatements.map(statement => {
            const apiStatement = apiAlignmentStatements.find(s => s.statement_id === statement.id);
            return {
                id: statement.id,
                statement: statement.statement,
                category: apiStatement?.properties?.category || "foundation",
                reverse: apiStatement?.properties?.is_reverse === true
            };
        });
        
        // Loop through statements and categorize them based on whether the user has responded
        alignmentStatements.forEach((statement) => {
            const statementId = statement.id;
            const alignmentData = getAlignmentData(statementId);
            
            if (!alignmentData) {
                return; // Skip this iteration if no data
            }
            
            const currentUserData = getCurrentUserAlignmentData(statementId);
            const hasResponded = currentUserData?.response !== null && 
                               currentUserData?.response !== undefined;
            
            // Create the statement object
            const statementObj = {
                id: statementId,
                statement: statement.statement,
                hasResponse: hasResponded
            };
            
            // Create the carousel item
            const carouselItem = (
                <CarouselItem key={statementId}>
                    <AlignmentTile
                        statement={statement.statement}
                        statementNumber={statementId}
                        response={alignmentData.response}
                        importance={alignmentData.importance}
                        note={alignmentData.note}
                        privacy={alignmentData.privacy}
                        currentUserResponse={currentUserData?.response}
                        currentUserImportance={currentUserData?.importance}
                        currentUserViewerRelation={profileUser?.viewer_relation as ViewerRelation}
                        isPanelOpen={isPanelOpen}
                        onPanelOpenChange={handlePanelOpenChange}
                        actorType="user"
                        onRespond={() => handleRespond(statementId)}
                        hasResponded={hasResponded}
                        statements={[...answeredStatements, ...unansweredStatements]} // This will be updated before rendering
                        onStatementChange={handleAlignmentStatementChange}
                        showSummaryOption={true}
                    />
                </CarouselItem>
            );
            
            // Add to the appropriate arrays based on whether the user has responded
            if (hasResponded) {
                answeredStatements.push(statementObj);
                answeredItems.push(carouselItem);
            } else {
                unansweredStatements.push(statementObj);
                unansweredItems.push(carouselItem);
            }
        });
        
        // Combine the arrays in the desired order: answered first, then unanswered
        const visibleStatements = [...answeredStatements, ...unansweredStatements];
        const renderedItems = [...answeredItems, ...unansweredItems];
        
        // Only return the carousel if we have items to display
        if (renderedItems.length === 0) {
            return null;
        }
        
        // Create the summary tile
        const summaryTile = (
            <CarouselItem key="summary">
                <AlignmentSummaryTile
                    statements={statementsWithCategory}
                    getTargetUserAlignmentData={getAlignmentData}
                    getCurrentUserAlignmentData={getCurrentUserAlignmentData}
                    onStatementSelect={handleAlignmentStatementChange}
                    targetUsername={profileUser?.username || ""}
                    currentUserViewerRelation={profileUser?.viewer_relation as ViewerRelation}
                />
            </CarouselItem>
        );
        
        // Update the statements prop for each carousel item with the complete list
        const finalRenderedItems = renderedItems.map((item, index) => {
            // Clone the item and update its props
            return React.cloneElement(item as React.ReactElement, {
                key: `statement-${index}`,
                children: React.cloneElement((item as React.ReactElement).props.children as React.ReactElement, {
                    statements: visibleStatements
                })
            });
        });
        
        // Add the summary tile at the beginning
        const allItems = [summaryTile, ...finalRenderedItems];
        
        return (
            <Carousel 
                className="w-full"
                setApi={setCarouselApi}
            >
                <CarouselContent className="">
                    {allItems}
                </CarouselContent>
                <div className="flex flex-row px-1 justify-between">
                    <CarouselPrevious size="lg" />
                    <CarouselNext size="lg" />
                </div>
            </Carousel>
        );
    };

    // Define tabs for profile sections
    const profileTabs = [
        { label: 'Background', value: 'background' },
        { label: 'Perspective', value: 'perspective' },
    ];

    // Handle tab change
    const handleProfileTabChange = (newTab: string) => {
        setSelectedProfileTab(newTab);
    };

    const handlePanelOpenChange = (open: boolean) => {
        setIsPanelOpen(open);
    };

    // Render content based on selected tab
    const renderProfileTabContent = () => {
        switch (selectedProfileTab) {
            case 'perspective':
                return (
                    <>
                        {renderAlignmentTiles()}
                    </>
                );
            case 'background':
                return (
                    <UserBackgroundTile aboutDetails={profileUser?.about || {}} />
                );
            default:
                return null;
        }
    };

    return (
        <div className="bg-eoe-bg dark:bg-eoe-dark-bg">
            <div className="max-w-5xl mx-auto px-2 py-2">
                {/* Profile Header */}
                <div className="flex flex-col items-left space-y-4">
                    {/* Avatar */}
                    <a href={`/user/${profileUser?.username}`} className="flex flex-row items-center gap-2 text-xl font-bold text-eoe-text-heading dark:text-eoe-dark-text-heading text-wrap break-all">
                        {profileUser?.username}
                    </a>
                    
                    <div className="flex-row grid grid-cols-3 sm:flex sm:flex-row items-center gap-2 sm:gap-16">
                        <div className="size-16 relative col-span-1">
                            <Avatar className="w-full h-full rounded-full">
                                <AvatarImage
                                    src={profileUser?.properties_public?.avatar_key ? `${IMG_CDN}/${profileUser.properties_public.avatar_key}` : undefined}
                                    alt={profileUser?.username}
                                    className="w-full h-full object-cover"
                                />
                                <AvatarFallback className="w-full h-full flex items-center justify-center text-2xl">
                                    {profileUser?.username.charAt(0).toUpperCase()}
                                </AvatarFallback>
                            </Avatar>
                        </div>

                        {/* Stats */}
                        <div className="flex flex-row gap-4 justify-between sm:justify-start col-span-2 text-sm">
                            <Link to={`/user/${profileUser?.username}/overview`}>
                                <div className="flex flex-col items-center">
                                    <span className="font-semibold text-eoe-text dark:text-eoe-dark-text">
                                        {postCount || 0}
                                </span>
                                <span className="text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                    spills
                                    </span>
                                </div>
                            </Link>
                            <button onClick={() => setShowFollowersModal(canViewFollow)} className={`${canViewFollow ? '' : 'cursor-default'}`}>
                                <div className="flex flex-col items-center">
                                    <span className="font-semibold text-eoe-text dark:text-eoe-dark-text">
                                        {followerCount}
                                    </span>
                                    <span className="text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                        followers
                                    </span>
                                </div>
                            </button>
                            <button onClick={() => setShowFollowingModal(canViewFollow)} className={`${canViewFollow ? '' : 'cursor-default'}`}>
                                <div className="flex flex-col items-center">
                                    <span className="font-semibold text-eoe-text dark:text-eoe-dark-text">
                                        {followingCount}
                                    </span>
                                    <span className="text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                        following
                                    </span>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Name and Username */}
                    <div className="text-left">
                        {profileUser?.display_name && (
                            <h1 className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                                {profileUser?.display_name}
                            </h1>
                        )}
                        {profileUser?.about?.about_bio_brief?.value && (
                            <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub whitespace-normal">
                                {profileUser?.about?.about_bio_brief?.value}
                            </p>
                        )}
                    </div>

                    {/* Edit Profile/Follow Button */}
                    {isOwnProfile ? (
                        <div className="flex flex-row gap-2">
                            <Link
                                to="/settings/profile"
                                className="flex flex-row shadow-sm items-center bg-transparent border-2 px-3 py-1 font-semibold text-eoe-text dark:text-eoe-dark-text border-eoe-text dark:border-eoe-dark-text hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover rounded-full text-sm w-fit"
                            >
                                <span className="text-eoe-text dark:text-eoe-dark-text">Edit profile</span>
                            </Link>
                        </div>
                    ) : (
                        <FollowButton
                            entityId={user.id}
                            entityType="user"
                            entityName={user.username}
                            isPrivate={user.properties_public?.is_private}
                            showAllOptions={true}
                            onFollowChange={(isFollowing) => {
                                // Optionally update follower count or other UI elements
                                if (profileUser) {
                                    setProfileUser({
                                        ...profileUser,
                                        follower_count: (profileUser.follower_count || 0) + (isFollowing ? 1 : -1)
                                    });
                                }
                            }}
                        />
                    )}

                    {/* Primary Issue */}
                    {primaryIssue && (
                        <EntityAvatar
                            entity={primaryIssue}
                            entityType="issue"
                            entityChip={true}
                            showLink={true}
                            showCloseChip={false}
                            className="pr-2 bg-eoe-bg-modal"
                            badgeText="#1 Issue"
                        />
                    )}
                    
                    {/* About Section */}
                    <DetailPanel title="About" className="mt-2 bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal"
                        defaultOpen={isPanelOpen}
                        onOpenChange={onOpenPanelChange}
                        headingContent={
                            <div className="flex flex-col items-start gap-2">
                                {profileUser?.about?.about_bio_links?.value && (() => {
                                    try {
                                        const links = profileUser.about.about_bio_links.value;
                                        if (links && links.length > 0) {
                                            return (
                                                <div className="flex flex-wrap gap-1">
                                                    {links.map((link: SocialLink, index: number) => (
                                                        <a
                                                            key={index}
                                                            href={link.url}
                                                            target="_blank"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                window.open(link.url, '_blank');
                                                            }}
                                                            rel="noopener noreferrer"
                                                            className="flex flex-row gap-1 items-center btn-secondary bg-slate-500 dark:bg-slate-800 hover:bg-slate-400 dark:hover:bg-slate-700 py-1 text-xs text-eoe-dark-text dark:text-eoe-dark-text-sub"
                                                        >
                                                            {getLinkIcon(link.type, link.url)}
                                                            {link.label}
                                                        </a>
                                                    ))}
                                                </div>
                                            );
                                        }
                                    } catch (error) {
                                        console.error('Error parsing social links:', error);
                                    }
                                    return null;
                                })()}
                            </div>
                        }
                    >
                        {/* Replace the perspective text with tabs */}
                        <div className="mb-2">
                            <ScrollableTabsList
                                tabs={profileTabs}
                                navlink={false}
                                selectedTab={selectedProfileTab}
                                pageTitle="User Profile"
                                onTabChange={handleProfileTabChange}
                                className="text-xs"
                            />
                        </div>
                        
                        {/* Render content based on selected tab */}
                        {renderProfileTabContent()}
                    </DetailPanel>
                </div>
            </div>

            {/* Followers Modal */}
            {showFollowersModal && (
                <FollowersModal
                    entityId={user.id}
                    entityType="user"
                    viewType="followers"
                    onClose={() => setShowFollowersModal(false)}
                />
            )}
            {showFollowingModal && (
                <FollowersModal
                    entityId={user.id}
                    entityType="user"
                    viewType="following"
                    onClose={() => setShowFollowingModal(false)}
                />
            )}
            {showAlignmentModal && (
                <UserAlignmentModal
                    onClose={() => setShowAlignmentModal(false)}
                    initialStatementId={statementNumber}
                />
            )}
        </div>
    );
};

export default UserProfilePlate; 